// src/components/forms/AnimalVictimInformation.jsx
import React, { useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    Autocomplete,
    FormControlLabel,
    Checkbox,
    MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    ANIMAL_CODES,
    GROUP_LABELS,
    getOptionLabel,
    getBreedsFor,
} from '../../constants/animalcodes';

const GROUP_OPTIONS = Object.keys(GROUP_LABELS).map((g) => ({
    value: g,
    label: GROUP_LABELS[g],
}));

const makeVictimRow = () => ({
    group: '',    // one of GROUP_OPTIONS.value
    type: null,   // { group, code, description }
    breed: '',    // string (freeSolo allowed)
});

export default function AnimalVictimInformation() {
    const theme = useTheme();
    const [count, setCount] = useState(1);
    const [victims, setVictims] = useState([makeVictimRow()]);

    const handleCountChange = (e) => {
        const v = Math.max(0, parseInt(e.target.value, 10) || 0);
        setCount(v);
        setVictims((prev) => {
            if (v > prev.length) {
                return [...prev, ...Array.from({ length: v - prev.length }, () => makeVictimRow())];
            }
            return prev.slice(0, v);
        });
    };

    const updateVictim = (idx, patch) => {
        setVictims((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };

    return (
        <Box mb={4}>
            {/* SC DPH�branded gradient header */}
            <Box
                sx={{
                    mb: 2,
                    p: 1,
                    borderRadius: 1,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }}
            >
                <Typography variant="h6" color="white">
                    Animal Victim Information
                </Typography>
            </Box>

            {/* Number of Victims */}
            <TextField
                label="Number of Animal Victim(s)"
                type="number"
                value={count}
                onChange={handleCountChange}
                InputProps={{ inputProps: { min: 0 } }}
                fullWidth
                margin="normal"
            />

            {Array.from({ length: count }).map((_, idx) => {
                const row = victims[idx] ?? makeVictimRow();
                const typeOptions = row.group
                    ? ANIMAL_CODES.filter((o) => o.group === row.group)
                    : [];
                const breedOptions = row.type ? getBreedsFor(row.type.code) : [];

                return (
                    <Box
                        key={idx}
                        mb={3}
                        p={2}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor: idx % 2 === 1 ? theme.palette.action.hover : 'transparent',
                        }}
                    >
                        <Typography variant="subtitle1" gutterBottom>
                            Victim Animal #{idx + 1}
                        </Typography>

                        <Grid container spacing={2}>
                            {/* Group (select first) */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Group"
                                    fullWidth
                                    value={row.group}
                                    onChange={(e) => {
                                        const g = e.target.value;
                                        // Changing group clears downstream fields
                                        updateVictim(idx, { group: g, type: null, breed: '' });
                                    }}
                                >
                                    {GROUP_OPTIONS.map((g) => (
                                        <MenuItem key={g.value} value={g.value}>
                                            {g.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Type (depends on Group) */}
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={typeOptions}
                                    getOptionLabel={getOptionLabel}
                                    isOptionEqualToValue={(a, b) => a.code === b.code && a.group === b.group}
                                    value={row.type}
                                    onChange={(_, val) => updateVictim(idx, { type: val, breed: '' })}
                                    disabled={!row.group}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Type"
                                            fullWidth
                                            placeholder={row.group ? 'Select type' : 'Select a group first'}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Breed (dynamic list when available, freeSolo otherwise) */}
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    freeSolo
                                    options={breedOptions}
                                    value={row.breed || null}
                                    onInputChange={(_, val) => updateVictim(idx, { breed: val })}
                                    onChange={(_, val) => updateVictim(idx, { breed: val || '' })}
                                    disabled={!row.type}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Breed"
                                            fullWidth
                                            placeholder={
                                                row.type
                                                    ? (breedOptions.length ? 'Select or type a breed' : 'Type breed')
                                                    : 'Select a type first'
                                            }
                                            helperText={
                                                row.type && !breedOptions.length
                                                    ? 'No preset list for this animal type; free text allowed.'
                                                    : ''
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Size/Color */}
                            <Grid item xs={12} sm={6}>
                                <TextField label="Size/Color" fullWidth />
                            </Grid>

                            {/* Sex (kept as checkboxes per your original) */}
                            <Grid item xs={12} sm={6}>
                                <Box display="flex" gap={2}>
                                    <FormControlLabel control={<Checkbox />} label="M" />
                                    <FormControlLabel control={<Checkbox />} label="F" />
                                    <FormControlLabel control={<Checkbox />} label="UKWN" />
                                </Box>
                            </Grid>

                            {/* Owner/Custodian & Animal Name */}
                            <Grid item xs={12} sm={6}>
                                <TextField label="Owner/Custodian Name" fullWidth />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Animal Name" fullWidth />
                            </Grid>

                            {/* Age */}
                            <Grid item xs={12} sm={6}>
                                <TextField label="Age" type="number" fullWidth />
                            </Grid>

                            {/* Address */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Address (If Not a Street Address, Give Directions)"
                                    fullWidth
                                />
                            </Grid>

                            {/* City / State / Zip */}
                            <Grid item xs={12} sm={4}>
                                <TextField label="City" fullWidth />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField label="State" defaultValue="SC" fullWidth />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField label="Zip" fullWidth />
                            </Grid>

                            {/* Phone / Alternate Phone / Email */}
                            <Grid item xs={12} sm={4}>
                                <TextField label="Phone" fullWidth placeholder="(123) 456-7890" />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Alternate Phone" fullWidth placeholder="(123) 456-7890" />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Email" type="email" fullWidth />
                            </Grid>

                            {/* Wound Location/Severity */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Wound Location/Severity"
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </Grid>

                            {/* Current Location & Health Status */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Current Location and Health Status of Animal"
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </Grid>

                            {/* Date of Vaccination */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Date of Vaccination"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {/* Vaccination Interval & Tag # (kept as in your original) */}
                            <Grid item xs={12} sm={3}>
                                <Box display="flex" gap={2}>
                                    <FormControlLabel control={<Checkbox />} label="1 yr" />
                                    <FormControlLabel control={<Checkbox />} label="3 yr" />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Tag #" fullWidth />
                            </Grid>

                            {/* Veterinarian Clinic/Name */}
                            <Grid item xs={12}>
                                <TextField label="Veterinarian Clinic/Name" fullWidth />
                            </Grid>
                        </Grid>
                    </Box>
                );
            })}
        </Box>
    );
}
